import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { Color3, Color4 } from "core/Maths/math.color";
import { ColorComponentEntry } from "./colorComponentEntry";
import { HexColor } from "./hexColor";
import "./colorPicker.scss";
import { Logger } from "core/Misc/logger";
/**
 * Class used to create a color picker
 */
export class ColorPicker extends React.Component {
    constructor(props) {
        super(props);
        if (this.props.color instanceof Color4) {
            this.state = { color: new Color3(this.props.color.r, this.props.color.g, this.props.color.b), alpha: this.props.color.a };
        }
        else {
            this.state = { color: this.props.color.clone(), alpha: 1 };
        }
        this._saturationRef = React.createRef();
        this._hueRef = React.createRef();
    }
    shouldComponentUpdate(nextProps, nextState) {
        return nextProps.color.toHexString() !== this.props.color.toHexString() || nextState.color.toHexString() !== this.props.color.toHexString();
    }
    onSaturationPointerDown(evt) {
        this._evaluateSaturation(evt);
        this._isSaturationPointerDown = true;
        evt.currentTarget.setPointerCapture(evt.pointerId);
    }
    onSaturationPointerUp(evt) {
        this._isSaturationPointerDown = false;
        evt.currentTarget.releasePointerCapture(evt.pointerId);
    }
    onSaturationPointerMove(evt) {
        if (!this._isSaturationPointerDown) {
            return;
        }
        this._evaluateSaturation(evt);
    }
    onHuePointerDown(evt) {
        this._evaluateHue(evt);
        this._isHuePointerDown = true;
        evt.currentTarget.setPointerCapture(evt.pointerId);
    }
    onHuePointerUp(evt) {
        this._isHuePointerDown = false;
        evt.currentTarget.releasePointerCapture(evt.pointerId);
    }
    onHuePointerMove(evt) {
        if (!this._isHuePointerDown) {
            return;
        }
        this._evaluateHue(evt);
    }
    _evaluateSaturation(evt) {
        const left = evt.nativeEvent.offsetX;
        const top = evt.nativeEvent.offsetY;
        const saturation = Math.min(1, Math.max(0.0001, left / this._saturationRef.current.clientWidth));
        const value = Math.min(1, Math.max(0.0001, 1 - top / this._saturationRef.current.clientHeight));
        if (this.props.debugMode) {
            Logger.Log("Saturation: " + saturation);
            Logger.Log("Value: " + value);
        }
        const hsv = this.state.color.toHSV();
        Color3.HSVtoRGBToRef(hsv.r, saturation, value, this.state.color);
        if (this.state.alpha === 0) {
            this.setState({ alpha: 1 });
        }
        this.setState({ color: this.state.color });
    }
    _evaluateHue(evt) {
        const left = evt.nativeEvent.offsetX;
        const hue = 360 * Math.min(0.9999, Math.max(0.0001, left / this._hueRef.current.clientWidth));
        if (this.props.debugMode) {
            Logger.Log("Hue: " + hue);
        }
        const hsv = this.state.color.toHSV();
        Color3.HSVtoRGBToRef(hue, Math.max(hsv.g, 0.01), Math.max(hsv.b, 0.01), this.state.color);
        this.setState({ color: this.state.color });
    }
    componentDidUpdate() {
        this.raiseOnColorChanged();
    }
    raiseOnColorChanged() {
        if (!this.props.onColorChanged) {
            return;
        }
        if (this.props.color instanceof Color4) {
            const newColor4 = Color4.FromColor3(this.state.color, this.state.alpha);
            this.props.onColorChanged(newColor4);
            return;
        }
        this.props.onColorChanged(this.state.color.clone());
    }
    render() {
        const color4 = Color4.FromColor3(this.state.color);
        color4.a = this.state.alpha;
        const colorHex = color4.toHexString();
        const hsv = this.state.color.toHSV();
        const colorRef = new Color3();
        Color3.HSVtoRGBToRef(hsv.r, 1, 1, colorRef);
        const colorHexRef = colorRef.toHexString();
        const hasAlpha = this.props.color instanceof Color4;
        return (_jsxs("div", { className: "color-picker-container" + (this.props.linearhint ? " with-hints" : ""), children: [_jsxs("div", { className: "color-picker-saturation", onPointerMove: (e) => this.onSaturationPointerMove(e), onPointerDown: (e) => this.onSaturationPointerDown(e), onPointerUp: (e) => this.onSaturationPointerUp(e), ref: this._saturationRef, style: {
                        background: colorHexRef,
                    }, children: [_jsx("div", { className: "color-picker-saturation-white" }), _jsx("div", { className: "color-picker-saturation-black" }), _jsx("div", { className: "color-picker-saturation-cursor", style: {
                                top: `${-(hsv.b * 100) + 100}%`,
                                left: `${hsv.g * 100}%`,
                            } })] }), _jsxs("div", { className: "color-picker-hue", children: [_jsx("div", { className: "color-picker-hue-color", style: {
                                background: colorHex,
                            } }), _jsx("div", { className: "color-picker-hue-slider", ref: this._hueRef, onPointerMove: (e) => this.onHuePointerMove(e), onPointerDown: (e) => this.onHuePointerDown(e), onPointerUp: (e) => this.onHuePointerUp(e), children: _jsx("div", { className: "color-picker-hue-cursor", style: {
                                    left: `${(hsv.r / 360.0) * 100}%`,
                                    border: `1px solid ` + colorHexRef,
                                } }) })] }), _jsxs("div", { className: "color-picker-rgb", children: [_jsx("div", { className: "red", children: _jsx(ColorComponentEntry, { lockObject: this.props.lockObject, label: "R", min: 0, max: 255, value: Math.round(this.state.color.r * 255), onChange: (value) => {
                                    this.state.color.r = value / 255.0;
                                    this.forceUpdate();
                                } }) }), _jsx("div", { className: "green", children: _jsx(ColorComponentEntry, { lockObject: this.props.lockObject, label: "G", min: 0, max: 255, value: Math.round(this.state.color.g * 255), onChange: (value) => {
                                    this.state.color.g = value / 255.0;
                                    this.forceUpdate();
                                } }) }), _jsx("div", { className: "blue", children: _jsx(ColorComponentEntry, { lockObject: this.props.lockObject, label: "B", min: 0, max: 255, value: Math.round(this.state.color.b * 255), onChange: (value) => {
                                    this.state.color.b = value / 255.0;
                                    this.forceUpdate();
                                } }) }), _jsx("div", { className: "alpha" + (hasAlpha ? "" : " grayed"), children: _jsx(ColorComponentEntry, { lockObject: this.props.lockObject, label: "A", min: 0, max: 255, value: Math.round(this.state.alpha * 255), onChange: (value) => {
                                    this.setState({ alpha: value / 255.0 });
                                    this.forceUpdate();
                                } }) })] }), _jsxs("div", { className: "color-picker-hex-row", children: [_jsxs("div", { className: "color-picker-hex", children: [_jsx("div", { className: "color-picker-hex-label", children: "Gamma Hex" }), _jsx("div", { className: "color-picker-hex-value", children: _jsx(HexColor, { lockObject: this.props.lockObject, expectedLength: hasAlpha ? 8 : 6, value: colorHex, onChange: (value) => {
                                            if (hasAlpha) {
                                                const color4 = Color4.FromHexString(value);
                                                this.setState({ color: new Color3(color4.r, color4.g, color4.b), alpha: color4.a });
                                            }
                                            else {
                                                this.setState({ color: Color3.FromHexString(value) });
                                            }
                                        } }) })] }), this.props.linearhint && (_jsxs("div", { className: "color-picker-hex", children: [_jsx("div", { className: "color-picker-hex-label", children: "Linear Hex" }), _jsx("div", { className: "color-picker-hex-value", children: _jsx(HexColor, { lockObject: this.props.lockObject, expectedLength: hasAlpha ? 8 : 6, value: Color4.FromHexString(colorHex).toLinearSpace().toHexString(), onChange: (value) => {
                                            if (hasAlpha) {
                                                const color4 = Color4.FromHexString(value).toGammaSpace();
                                                this.setState({ color: new Color3(color4.r, color4.g, color4.b), alpha: color4.a });
                                            }
                                            else {
                                                this.setState({ color: Color3.FromHexString(value).toGammaSpace() });
                                            }
                                        } }) })] }))] }), this.props.linearhint && (_jsx("div", { className: "color-picker-warning", children: "(Note: This color is stored in linear but converted to gamma to be displayed here. To set color in code, use the linear hex above or use gamma.toLinearSpace()" }))] }));
    }
}
//# sourceMappingURL=colorPicker.js.map